import { useQuery } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import {
  TICKET_DISPATCH_STATUS_LABELS,
  dispatchReasonLabel,
  dispatchReasonOf,
  type TicketDispatchStatus,
} from '@erp/shared';
import { Badge, type BadgeProps } from '@/components/ui/misc';
import { formatDateTime } from '@/lib/utils';
import { useTerms } from '@/shared/tenant-profile';
import { ticketDispatchQuery } from './dispatch-queries';

/** a cor do status do disparo — o selo, o bloco do ticket e a lista "Disparos" */
export const DISPATCH_STATUS_VARIANT: Record<TicketDispatchStatus, BadgeProps['variant']> = {
  pending: 'warning',
  sending: 'info',
  sent: 'info',
  delivered: 'info',
  failed: 'destructive',
  skipped: 'default',
  replied: 'success',
};

/**
 * Selo do disparo no cabeçalho do ticket (spec 014): "Na fila de disparo",
 * "Disparo realizado", "Disparo falhou: motivo", "Cliente respondeu". Some
 * quando o ticket nunca entrou na fila — e quando saiu dela sem nunca ter
 * sido disparado (ignorado), que é ruído para quem está atendendo.
 */
export function TicketDispatchBadge({ ticketNumber }: { ticketNumber: number }) {
  const q = useQuery(ticketDispatchQuery(ticketNumber));
  const terms = useTerms();
  const dispatch = q.data?.dispatch ?? null;
  if (!dispatch || dispatch.status === 'skipped') return null;

  // spec 025, fase 3: o pendente com motivo voltou para a fila (adiado pela Blip,
  // esperando o TiFlux) — o motivo aparece como no falhou
  // sem telefone, diz onde o disparo procurou (a mesma lista que o envio percorre)
  const reason = dispatchReasonLabel(dispatchReasonOf(dispatch), terms, q.data?.phoneSources);
  const when = dispatch.repliedAt ?? dispatch.failedAt ?? dispatch.sentAt ?? dispatch.enqueuedAt;
  return (
    <Badge
      variant={DISPATCH_STATUS_VARIANT[dispatch.status]}
      title={`${TICKET_DISPATCH_STATUS_LABELS[dispatch.status]} — ${formatDateTime(when)}${
        dispatch.manual ? ' (manual)' : ''
      }`}
    >
      <Send className="size-3" />
      {TICKET_DISPATCH_STATUS_LABELS[dispatch.status]}
      {reason ? `: ${reason}` : ''}
    </Badge>
  );
}
